import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { fmtFCFA } from "../lib/format";
import { Confirm } from "./Shared";
import { toast } from "sonner";
import { ChevronsUpDown, Plus, Settings2, Trash2, Save, Tags } from "lucide-react";

export const useMaterials = () => useQuery({ queryKey: ["estimate-materials"], queryFn: () => api.get("/estimate-materials").then((r) => r.data) });
const errMsg = (e, d) => formatApiErrorDetail(e.response?.data?.detail) || d;
const BASE_CATS = ["Cables", "Switches & Sockets", "Distribution & Earthing"];
const catOf = (m) => (m.category || "").trim() || "Other";
const catRank = (c) => (BASE_CATS.includes(c) ? BASE_CATS.indexOf(c) : c === "Other" ? 999 : 100);
export const groupMaterials = (mats) => {
  const g = {};
  mats.forEach((m) => { (g[catOf(m)] = g[catOf(m)] || []).push(m); });
  return Object.keys(g).sort((a, b) => catRank(a) - catRank(b) || a.localeCompare(b)).map((c) => [c, g[c]]);
};
const allCats = (mats, list = []) => [...new Set([...list.map((c) => c.name), ...mats.map(catOf)])].sort((a, b) => catRank(a) - catRank(b) || a.localeCompare(b));
export const useCategories = () => useQuery({ queryKey: ["estimate-material-categories"], queryFn: () => api.get("/estimate-material-categories").then((r) => r.data) });

const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

const CategoryRow = ({ c, onSaved }) => {
  const [name, setName] = useState(c.name);
  const locked = c.name_lower === "other";
  const save = async () => {
    try { const { data } = await api.put(`/estimate-material-categories/${c.id}`, { name }); toast.success(`Category renamed${data.materials_updated ? ` (${plural(data.materials_updated, "material")} updated)` : ""}.`); onSaved(); }
    catch (e) { toast.error(errMsg(e, "Could not rename category.")); }
  };
  const del = async () => {
    try { const { data } = await api.delete(`/estimate-material-categories/${c.id}`); toast.success(`Category deleted${data.materials_moved_to_other ? ` — ${plural(data.materials_moved_to_other, "material")} moved to Other` : ""}.`); onSaved(); }
    catch (e) { toast.error(errMsg(e, "Could not delete category.")); }
  };
  return (
    <div className="flex items-center gap-2" data-testid={`category-row-${c.id}`}>
      <Input value={name} disabled={locked} onChange={(e) => setName(e.target.value)} className="h-8" data-testid={`category-name-${c.id}`} />
      <span className="text-xs text-slate-400 w-16 shrink-0">{c.material_count} item{c.material_count === 1 ? "" : "s"}</span>
      {!locked && <>
        <button onClick={save} disabled={!name.trim() || name.trim() === c.name} title="Save" data-testid={`category-save-${c.id}`} className="p-1 text-[#1E3A8A] disabled:text-slate-300"><Save className="h-4 w-4" /></button>
        <Confirm onConfirm={del} title="Delete category?" description={`"${c.name}" will be removed. Its ${c.material_count} material(s) move to Other.`}
          trigger={<button title="Delete" data-testid={`category-delete-${c.id}`} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>} />
      </>}
      {locked && <span className="text-[11px] text-slate-400 w-12">fallback</span>}
    </div>
  );
};

const ManageCategories = ({ list, onChanged }) => {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/estimate-material-categories", { name }); setName(""); toast.success("Category added."); onChanged(); }
    catch (e) { toast.error(errMsg(e, "Could not add category.")); }
  };
  return (
    <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-3 space-y-2" data-testid="manage-categories-panel">
      <div className="flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="New category name" className="h-8 bg-white" data-testid="category-new-name" />
        <Button size="sm" onClick={add} data-testid="category-add-btn" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2 max-h-48 overflow-y-auto pr-1">
        {[...list].sort((a, b) => catRank(a.name) - catRank(b.name) || a.name.localeCompare(b.name)).map((c) => <CategoryRow key={`${c.id}-${c.name}-${c.material_count}`} c={c} onSaved={onChanged} />)}
      </div>
    </div>
  );
};
export const UNITS = ["Roll", "Pkt", "ML", "Piece"];
const selCls = "h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring";

export const UnitSelect = ({ value, onChange, testid }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={selCls}>
    <option value="">Unit…</option>
    {value && !UNITS.includes(value) && <option value={value}>{value}</option>}
    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
  </select>
);

const CategorySelect = ({ value, onChange, cats, testid }) => {
  const [typing, setTyping] = useState(false);
  if (typing && !cats.includes(value)) {
    return <Input autoFocus value={value} onChange={(e) => onChange(e.target.value)} placeholder="New category name" data-testid={`${testid}-input`}
      onBlur={() => { if (!value.trim()) { onChange(""); setTyping(false); } }} onKeyDown={(e) => { if (e.key === "Escape") { onChange(""); setTyping(false); } }} />;
  }
  return (
    <select value={value || ""} onChange={(e) => { if (e.target.value === "__new") { onChange(""); setTyping(true); } else onChange(e.target.value); }} data-testid={testid} className={selCls}>
      <option value="">Category…</option>
      {cats.map((c) => <option key={c} value={c}>{c}</option>)}
      <option value="__new">+ New category…</option>
    </select>
  );
};

export const MaterialPicker = ({ onPick }) => {
  const qc = useQueryClient();
  const { data: mats = [] } = useMaterials();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const exact = mats.some((m) => m.name.toLowerCase() === search.trim().toLowerCase());
  const pick = (m) => { onPick(m); setOpen(false); setSearch(""); };
  const createAndPick = async () => {
    try {
      const { data } = await api.post("/estimate-materials", { name: search.trim(), unit: "", unit_cost: 0 });
      qc.invalidateQueries({ queryKey: ["estimate-materials"] });
      toast.success("Material added — set its unit and price under Manage materials."); pick(data);
    } catch (e) { toast.error(errMsg(e, "Could not add material.")); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-72 justify-between font-normal text-slate-600" data-testid="estimate-material-picker">
          + Quick add material… <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search materials…" value={search} onValueChange={setSearch} data-testid="estimate-material-search" />
          <CommandList className="max-h-72">
            <CommandEmpty>No material found.</CommandEmpty>
            {groupMaterials(mats).map(([cat, items]) => (
              <CommandGroup key={cat} heading={cat} data-testid={`estimate-material-group-${cat}`}>
                {items.map((m) => (
                  <CommandItem key={m.id} value={`${m.name} ${cat}`} onSelect={() => pick(m)} data-testid={`estimate-material-option-${m.id}`}>
                    <span className="flex-1">{m.name}</span>
                    <span className="text-xs text-slate-400">{m.unit || "—"} · {fmtFCFA(m.unit_cost || 0)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
          {search.trim() && !exact && (
            <button onClick={createAndPick} data-testid="estimate-material-create" className="w-full border-t border-slate-100 px-3 py-2 text-left text-sm text-[#1E3A8A] hover:bg-slate-50 flex items-center gap-2">
              <Plus className="h-4 w-4" /> Add "{search.trim()}" to the list
            </button>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const MaterialRow = ({ m, cats, onSaved }) => {
  const [f, setF] = useState({ name: m.name, unit: m.unit || "", unit_cost: m.unit_cost ?? 0, category: catOf(m) });
  const dirty = f.name !== m.name || f.unit !== (m.unit || "") || String(f.unit_cost) !== String(m.unit_cost ?? 0) || f.category !== catOf(m);
  const save = async () => {
    try { await api.put(`/estimate-materials/${m.id}`, { ...f, unit_cost: parseFloat(f.unit_cost) || 0 }); toast.success("Material updated."); onSaved(); }
    catch (e) { toast.error(errMsg(e, "Could not update material.")); }
  };
  const del = async () => {
    try { await api.delete(`/estimate-materials/${m.id}`); toast.success("Material removed."); onSaved(); }
    catch (e) { toast.error(errMsg(e, "Could not remove material.")); }
  };
  return (
    <tr className="border-t border-slate-100" data-testid={`material-row-${m.id}`}>
      <td className="px-2 py-1.5"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`material-name-${m.id}`} /></td>
      <td className="px-2 py-1.5 w-48"><CategorySelect value={f.category} onChange={(v) => setF({ ...f, category: v })} cats={cats} testid={`material-category-${m.id}`} /></td>
      <td className="px-2 py-1.5 w-28"><UnitSelect value={f.unit} onChange={(v) => setF({ ...f, unit: v })} testid={`material-unit-${m.id}`} /></td>
      <td className="px-2 py-1.5 w-32"><Input type="number" min="0" step="any" className="text-right" value={f.unit_cost} onChange={(e) => setF({ ...f, unit_cost: e.target.value })} data-testid={`material-cost-${m.id}`} /></td>
      <td className="px-2 py-1.5 w-20 whitespace-nowrap text-right">
        <button onClick={save} disabled={!dirty} title="Save" data-testid={`material-save-${m.id}`} className="p-1 text-[#1E3A8A] disabled:text-slate-300"><Save className="h-4 w-4" /></button>
        <Confirm onConfirm={del} title="Remove material?" description={`"${m.name}" will be removed from the shared list. Existing estimates are not affected.`}
          trigger={<button title="Remove" data-testid={`material-delete-${m.id}`} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>} />
      </td>
    </tr>
  );
};

export const ManageMaterials = () => {
  const qc = useQueryClient();
  const { data: mats = [] } = useMaterials();
  const { data: catList = [] } = useCategories();
  const [open, setOpen] = useState(false);
  const [showCats, setShowCats] = useState(false);
  const [n, setN] = useState({ name: "", category: "", unit: "", unit_cost: "" });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["estimate-materials"] }); qc.invalidateQueries({ queryKey: ["estimate-material-categories"] }); };
  const cats = allCats(mats, catList);
  const add = async () => {
    if (!n.name.trim()) { toast.error("Enter a material name."); return; }
    try { await api.post("/estimate-materials", { ...n, unit_cost: parseFloat(n.unit_cost) || 0 }); setN({ name: "", category: n.category, unit: "", unit_cost: "" }); toast.success("Material added."); refresh(); }
    catch (e) { toast.error(errMsg(e, "Could not add material.")); }
  };
  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)} data-testid="estimate-manage-materials" className="text-slate-600"><Settings2 className="h-4 w-4 mr-2" /> Manage materials</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="manage-materials-dialog">
          <DialogHeader><DialogTitle>Materials list</DialogTitle></DialogHeader>
          <div className="flex items-start justify-between gap-3 -mt-2">
            <p className="text-sm text-slate-500">Shared by everyone who creates estimates. Picking a material fills its unit and default price.</p>
            <Button size="sm" variant="outline" onClick={() => setShowCats((s) => !s)} data-testid="toggle-manage-categories" className="shrink-0"><Tags className="h-4 w-4 mr-1.5" />{showCats ? "Hide categories" : "Edit categories"}</Button>
          </div>
          {showCats && <ManageCategories list={catList} onChanged={refresh} />}
          <div className="max-h-[55vh] overflow-auto rounded-lg border border-slate-100">
            <table className="w-full min-w-[700px] text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0 z-10">
                <tr><th className="px-3 py-2 text-left">Material</th><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-left">Unit</th><th className="px-3 py-2 text-right">Default price</th><th></th></tr>
              </thead>
              <tbody>
                <tr className="bg-blue-50/40">
                  <td className="px-2 py-1.5"><Input value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} placeholder="New material name" data-testid="material-new-name" /></td>
                  <td className="px-2 py-1.5"><CategorySelect value={n.category} onChange={(v) => setN({ ...n, category: v })} cats={cats} testid="material-new-category" /></td>
                  <td className="px-2 py-1.5"><UnitSelect value={n.unit} onChange={(v) => setN({ ...n, unit: v })} testid="material-new-unit" /></td>
                  <td className="px-2 py-1.5"><Input type="number" min="0" step="any" className="text-right" value={n.unit_cost} onChange={(e) => setN({ ...n, unit_cost: e.target.value })} placeholder="0" data-testid="material-new-cost" /></td>
                  <td className="px-2 py-1.5 text-right"><Button size="sm" onClick={add} data-testid="material-add-btn" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4" /></Button></td>
                </tr>
                {groupMaterials(mats).map(([cat, items]) => (
                  <React.Fragment key={cat}>
                    <tr className="bg-slate-50/70" data-testid={`material-group-${cat}`}><td colSpan={5} className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-[#1E3A8A]">{cat} <span className="text-slate-400 font-normal">({items.length})</span></td></tr>
                    {items.map((m) => <MaterialRow key={`${m.id}-${m.name}-${m.unit}-${m.unit_cost}-${m.category}`} m={m} cats={cats} onSaved={refresh} />)}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
